import {
  HeartIcon,
  UsersIcon,
  GlobeAltIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

export default function About() {
  const teamMembers = [
    {
      name: "Dr. Priya Fernando",
      role: "Founder & CEO",
      description: "Nutritionist with 15+ years experience in food security",
      image:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
    },
    {
      name: "Rajesh Kumar",
      role: "CTO",
      description: "Tech entrepreneur passionate about social impact",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
    },
    {
      name: "Sarah Perera",
      role: "Operations Director",
      description: "Logistics expert with background in community development",
      image:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
    },
  ];

  const values = [
    {
      title: "Food Security",
      description: "Ensuring no one goes hungry in our communities",
      icon: <HeartIcon className="w-6 h-6" />,
    },
    {
      title: "Environmental Care",
      description: "Reducing food waste and environmental impact",
      icon: <GlobeAltIcon className="w-6 h-6" />,
    },
    {
      title: "Community Building",
      description: "Connecting people through shared meals and kindness",
      icon: <UsersIcon className="w-6 h-6" />,
    },
    {
      title: "Transparency",
      description: "Open and honest in all our operations",
      icon: <CheckCircleIcon className="w-6 h-6" />,
    },
  ];

  const stats = [
    {
      label: "Meals Shared",
      value: "50,000+",
      icon: <HeartIcon className="w-5 h-5" />,
    },
    {
      label: "Active Members",
      value: "2,500+",
      icon: <UsersIcon className="w-5 h-5" />,
    },
    {
      label: "Partner Businesses",
      value: "150+",
      icon: <BuildingStorefrontIcon className="w-5 h-5" />,
    },
    {
      label: "Cities Covered",
      value: "25",
      icon: <GlobeAltIcon className="w-5 h-5" />,
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/20">
      {/* Hero Section */}
      <div className="py-16 text-white bg-gradient-to-br from-emerald-600 via-teal-700 to-cyan-600">
        <div className="px-4 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
          <h1 className="mb-6 text-3xl font-bold md:text-4xl lg:text-5xl">
            About MealBridge
          </h1>
          <p className="max-w-3xl mx-auto text-lg text-white/95 font-medium md:text-xl">
            We're on a mission to eliminate food waste and hunger in Sri Lanka
            by connecting surplus food with those who need it most.
          </p>
        </div>
      </div>

      <div className="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Mission Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Our Mission
            </h2>
            <p className="max-w-3xl mx-auto text-base text-slate-600 font-medium">
              To create a sustainable food sharing ecosystem that reduces waste,
              fights hunger, and builds stronger communities across Sri Lanka.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, index) => (
              <div key={index} className="text-center card-dashboard">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl shadow-lg">
                  {stat.icon}
                </div>
                <h3 className="mb-2 text-2xl font-bold text-slate-900">
                  {stat.value}
                </h3>
                <p className="text-sm font-medium text-slate-600">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Story Section */}
        <section className="mb-16">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <div>
              <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
                Our Story
              </h2>
              <div className="space-y-4 text-slate-600">
                <p>
                  MealBridge was born from a simple observation: while
                  restaurants and bakeries were throwing away perfectly good
                  food, families in our communities were struggling to put meals
                  on their tables.
                </p>
                <p>
                  Founded in 2023 by Dr. Priya Fernando, a nutritionist with
                  over 15 years of experience in food security, MealBridge
                  started as a small initiative in Colombo and has grown into a
                  nationwide movement.
                </p>
                <p>
                  Today, we connect thousands of donors, recipients, and
                  volunteers across all 25 districts of Sri Lanka, creating a
                  sustainable ecosystem that benefits everyone involved.
                </p>
              </div>
            </div>
            <div className="card-dashboard">
              <div className="p-6">
                <h3 className="mb-4 text-lg font-bold text-slate-900">
                  Our Impact in Numbers
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                      Food Waste Prevented
                    </span>
                    <span className="text-lg font-bold text-emerald-600">
                      15,000 kg
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                      CO₂ Emissions Saved
                    </span>
                    <span className="text-lg font-bold text-emerald-600">
                      3,200 kg
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">
                      Volunteer Hours
                    </span>
                    <span className="text-lg font-bold text-emerald-600">
                      8,500+
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Success Rate</span>
                    <span className="text-lg font-bold text-emerald-600">
                      94%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Our Values
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              These core values guide everything we do at MealBridge
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {values.map((value, index) => (
              <div key={index} className="text-center card-dashboard">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-xl shadow-lg">
                  {value.icon}
                </div>
                <h3 className="mb-3 text-lg font-bold text-slate-900">
                  {value.title}
                </h3>
                <p className="text-sm text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Team Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="mb-6 text-2xl font-bold text-slate-900 lg:text-3xl">
              Meet Our Team
            </h2>
            <p className="max-w-2xl mx-auto text-base text-slate-600 font-medium">
              The passionate individuals behind MealBridge's success
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {teamMembers.map((member, index) => (
              <div key={index} className="text-center card-dashboard">
                <div className="w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="mb-2 text-lg font-bold text-slate-900">
                  {member.name}
                </h3>
                <p className="mb-3 text-sm font-medium text-emerald-600">
                  {member.role}
                </p>
                <p className="text-sm text-slate-600">{member.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action */}
        <section className="text-center">
          <div className="card-dashboard bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200">
            <h2 className="mb-4 text-xl font-bold text-slate-900 lg:text-2xl">
              Join Our Mission
            </h2>
            <p className="max-w-2xl mx-auto mb-8 text-sm text-slate-700 font-medium">
              Whether you're a business looking to reduce waste, someone who
              needs food assistance, or a volunteer wanting to make a
              difference, there's a place for you in the MealBridge community.
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <Link
                to="/auth"
                className="px-6 py-3 text-sm font-medium text-white bg-emerald-600 rounded-lg border border-emerald-600 text-center"
              >
                Get Started Today
              </Link>
              <Link
                to="/help"
                className="px-6 py-3 text-sm font-medium text-slate-700 bg-white rounded-lg border border-slate-300 text-center"
              >
                Learn More
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
